import { addDays, format, parseISO } from 'date-fns';
import type { Card } from '@/types';
import { DONE } from './cardStatus';

export type CardDateGroup = { key: string; label: string; cards: Card[] };

/**
 * Group open cards by due date for the job list.
 * Completed (Done) cards are pulled out into a trailing "Completed" column
 * (recent first), regardless of their due date — the backend only returns them
 * for 24h after completion.
 * Today is always first (even when empty) and also holds past-due and undated cards.
 * Tomorrow appears only when it has cards; later dates follow in ascending order.
 * `limit_date` is an ISO datetime; group by its local calendar day. Never `new Date(str)`.
 */
const dayKey = (limitDate: string | null | undefined): string | null =>
    limitDate ? format(parseISO(limitDate), 'yyyy-MM-dd') : null;

export function groupCardsByDate(cards: Card[], today: Date = new Date()): CardDateGroup[] {
    const todayKey = format(today, 'yyyy-MM-dd');
    const tomorrowKey = format(addDays(today, 1), 'yyyy-MM-dd');

    const completed: Card[] = [];
    const pastDue: Card[] = [];
    const dueToday: Card[] = [];
    const undated: Card[] = [];
    const later = new Map<string, Card[]>();

    for (const card of cards) {
        const key = dayKey(card.limit_date);
        if (card.status === DONE) completed.push(card);
        else if (!key) undated.push(card);
        else if (key < todayKey) pastDue.push(card);
        else if (key === todayKey) dueToday.push(card);
        else later.set(key, [...(later.get(key) ?? []), card]);
    }

    // Stable sort keeps the API order (limit_date ASC, id ASC) for equal dates.
    pastDue.sort((a, b) => (a.limit_date ?? '').localeCompare(b.limit_date ?? ''));
    // Most recently completed first.
    completed.sort((a, b) => (b.due_complete_at ?? '').localeCompare(a.due_complete_at ?? ''));

    const groups: CardDateGroup[] = [
        { key: todayKey, label: 'Today', cards: [...pastDue, ...dueToday, ...undated] },
    ];

    for (const key of [...later.keys()].sort()) {
        groups.push({
            key,
            label: key === tomorrowKey ? 'Tomorrow' : format(parseISO(key), 'EEEE, MMM d'),
            cards: later.get(key) ?? [],
        });
    }

    if (completed.length > 0) {
        groups.push({ key: 'completed', label: 'Completed', cards: completed });
    }

    return groups;
}
